import {
  DetectedDocumentKind,
  DocumentInputType,
} from '../validators/cpf-cnpjs-validators';

/** Formatted CPF display length (`000.000.000-00`). */
export const cpfFormattedMaxLength = 14;

/** ngx-mask patterns for Brazilian tax IDs (formatted length includes punctuation). */
export const cpfInputMask = '000.000.000-00';
export const cnpjInputMask = '00.000.000/0000-00';

/**
 * Static `maxlength` for CPF-or-CNPJ fields (formatted CNPJ is the longest: 18).
 * Do not reassign this in `valueChanges` — only the **mask** should follow input.
 */
export const cpfCnpjFormattedMaxLength = 18;

const cpfDigitCount = 11;

/**
 * Live ngx-mask for PIX keys typed as document (not for holder CPF/CNPJ fields).
 * CPF mask through 11 digits; CNPJ mask from the 12th digit onward.
 * Holder document inputs should use {@link cpfCnpjNoLiveMask} and {@link formatCpfCnpjDisplay} on blur.
 */
export function cpfCnpjMask(value: string | null | undefined): string {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length > cpfDigitCount ? cnpjInputMask : cpfInputMask;
}

/** Use on holder CPF/CNPJ fields: type freely, format on blur. */
export const cpfCnpjNoLiveMask = '';

/** Cosmetic CPF/CNPJ formatting for blur (no ngx-mask while typing). */
export function formatCpfCnpjDisplay(raw: string | null | undefined): string {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) {
    return '';
  }
  const d = trimmed.replace(/\D/g, '');
  if (d.length === 14) {
    return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  if (d.length === 11) {
    return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return trimmed;
}

/**
 * Infers CPF vs CNPJ from digits and {@link DocumentInputType}.
 * In `auto` mode: 1–11 digits → CPF, 12+ → CNPJ.
 */
export function detectDocumentKind(
  raw: string | null | undefined,
  mode: DocumentInputType,
): DetectedDocumentKind | null {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits.length) {
    return null;
  }
  if (mode === 'cpf') {
    return 'cpf';
  }
  if (mode === 'cnpj') {
    return 'cnpj';
  }
  return digits.length <= cpfDigitCount ? 'cpf' : 'cnpj';
}

/** Max input length for `gatewee-document-input` by mode (formatted). */
export function documentInputMaxLength(type: DocumentInputType): number {
  return type === 'cpf' ? cpfFormattedMaxLength : cpfCnpjFormattedMaxLength;
}

/** Cosmetic formatting on blur for document inputs (no live ngx-mask). */
export function formatDocumentDisplay(
  raw: string | null | undefined,
  type: DocumentInputType,
): string {
  if (type === 'auto') {
    return formatCpfCnpjDisplay(raw);
  }

  const trimmed = String(raw ?? '').trim();
  if (!trimmed) {
    return '';
  }

  const d = trimmed.replace(/\D/g, '');

  if (type === 'cpf') {
    if (d.length === 11) {
      return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return trimmed;
  }

  if (d.length === 14) {
    return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return trimmed;
}

/**
 * When PIX key type is **document**, same dynamic CPF/CNPJ mask; otherwise no mask.
 */
export function pixKeyDocumentMask(
  pixKeyType: string | null | undefined,
  rawPixValue: string | null | undefined,
): string {
  if (String(pixKeyType ?? '') !== 'document') {
    return '';
  }
  return cpfCnpjMask(rawPixValue);
}
